import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Thermometer, AlertTriangle, TrendingUp, TrendingDown, Activity, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import DataSourceBadge from '../components/common/DataSourceBadge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TestResults = ({ user }) => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    fetchTestResults();
  }, []);

  const fetchTestResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/test-results`);
      setTestResults(response.data);
    } catch (error) {
      console.error('Error fetching product insights:', error);
      toast.error('Failed to load product insights');
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now - then) / 1000);
    
    if (seconds < 60) return `${seconds} sec ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const handleProductSelect = (productName) => {
    setSelectedProducts(prev => {
      if (prev.includes(productName)) {
        return prev.filter(p => p !== productName);
      } else {
        if (prev.length >= 4) {
          toast.warning('Maximum 4 products can be compared');
          return prev;
        }
        return [...prev, productName];
      }
    });
  };

  const handleCompare = async () => {
    if (selectedProducts.length < 2) {
      toast.error('Please select at least 2 products to compare');
      return;
    }

    try {
      const productNames = selectedProducts.join(',');
      const response = await axios.get(`${BACKEND_URL}/api/test-results/compare`, {
        params: { product_names: productNames }
      });
      setComparisonData(response.data);
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } catch (error) {
      console.error('Error comparing products:', error);
      toast.error('Failed to compare products');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'warning':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'critical':
        return 'text-rose-600 bg-rose-50 border-rose-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'normal':
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'critical':
        return <AlertTriangle className="w-5 h-5 text-rose-600" />;
      default:
        return <Activity className="w-5 h-5 text-slate-600" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading product insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="test-results-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>
            Product Insights
          </h1>
          <DataSourceBadge source="live" label="Live Sensor Data" />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-slate-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <Package className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider truncate">Monitored Products</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{testResults.length}</p>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-slate-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider truncate">Normal</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-emerald-600">
            {testResults.filter(r => r.overall_status === 'normal').length}
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-slate-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider truncate">Warnings</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-amber-600">
            {testResults.filter(r => r.overall_status === 'warning').length}
          </p>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-4 lg:p-6 border border-slate-200">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider truncate">Critical Alerts</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-rose-600">
            {testResults.filter(r => r.overall_status === 'critical').length}
          </p>
        </div>
      </div>

      {/* Cold Chain Status */}
      {testResults.length > 0 && (
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 sm:p-6 lg:p-8 border border-slate-200">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-4 sm:mb-6" style={{ fontFamily: 'Manrope' }}>
            Cold Chain Status
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200">
              <p className="text-[10px] sm:text-sm text-slate-600 mb-1">Active Sensors</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {testResults.reduce((sum, p) => sum + (p.reading_count > 0 ? 1 : 0), 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200">
              <p className="text-[10px] sm:text-sm text-slate-600 mb-1">Sensor Readings Today</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">
                {testResults.reduce((sum, p) => sum + p.reading_count, 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200">
              <p className="text-[10px] sm:text-sm text-slate-600 mb-1">Last Sensor Update</p>
              <p className="text-base sm:text-2xl font-bold text-emerald-600 break-words">
                {(() => {
                  const latestTimestamp = testResults
                    .map(p => new Date(p.latest_reading_timestamp))
                    .reduce((latest, current) => current > latest ? current : latest, new Date(0));
                  return getTimeAgo(latestTimestamp.toISOString());
                })()}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3 sm:p-4 border border-slate-200">
              <p className="text-[10px] sm:text-sm text-slate-600 mb-1">Monitoring Coverage</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                {testResults.length > 0
                  ? Math.round((testResults.filter(p => p.reading_count > 0).length / testResults.length) * 100)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Latest Sensor Activity */}
      {testResults.length > 0 && (
        <div className="bg-white rounded-xl p-4 sm:p-6 lg:p-8 border border-slate-200">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Manrope' }}>
            Latest Sensor Activity
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 mb-1">
            Most recent cold-chain readings received across monitored products
          </p>
          <p className="text-[11px] sm:text-xs text-slate-500 mb-4 sm:mb-6">
            <strong>Highest Alert Level</strong> reflects the most severe status recorded during the current monitoring session, not just the latest reading.
          </p>

          {/* Mobile: card list, Desktop: table */}
          <div className="md:hidden space-y-2" data-testid="sensor-activity-cards">
            {testResults
              .sort((a, b) => new Date(b.latest_reading_timestamp) - new Date(a.latest_reading_timestamp))
              .slice(0, 10)
              .map((product) => {
                const timeAgo = getTimeAgo(product.latest_reading_timestamp);
                return (
                  <div
                    key={product.product_name}
                    className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{product.product_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{product.species_or_category}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{timeAgo}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-slate-900">{product.latest_temperature.toFixed(1)}°C</p>
                      <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium uppercase ${getStatusColor(product.overall_status)}`}>
                        {product.overall_status}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Latest Temp
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Highest Alert Level
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {testResults
                  .sort((a, b) => new Date(b.latest_reading_timestamp) - new Date(a.latest_reading_timestamp))
                  .slice(0, 10)
                  .map((product) => {
                    const timeAgo = getTimeAgo(product.latest_reading_timestamp);
                    return (
                      <tr key={product.product_name} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-semibold text-slate-900">{product.product_name}</p>
                            <p className="text-xs text-slate-500">{product.species_or_category}</p>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="text-base font-bold text-slate-900">
                            {product.latest_temperature.toFixed(1)}°C
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium uppercase ${getStatusColor(product.overall_status)}`}>
                            {product.overall_status}
                          </span>
                        </td>
                        <td className="text-right py-3 px-4">
                          <span className="text-sm text-slate-600">{timeAgo}</span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Comparison Controls */}
      {testResults.length > 1 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-1">Compare Products</h3>
              <p className="text-xs sm:text-sm text-blue-700">
                Select 2-4 products to compare side by side ({selectedProducts.length} selected)
              </p>
            </div>
            <Button
              onClick={handleCompare}
              disabled={selectedProducts.length < 2}
              className="bg-blue-600 hover:bg-blue-700 text-white h-10 self-start sm:self-auto"
            >
              Compare Selected
            </Button>
          </div>
        </div>
      )}

      {/* Test Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {testResults.map((product) => (
          <div
            key={product.product_name}
            className={`bg-white rounded-xl p-4 sm:p-6 border-2 transition-all cursor-pointer ${
              selectedProducts.includes(product.product_name)
                ? 'border-blue-500 shadow-lg'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => handleProductSelect(product.product_name)}
          >
            <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-slate-900 mb-1 break-words" style={{ fontFamily: 'Manrope' }}>
                  {product.product_name}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 truncate">{product.species_or_category}</p>
                <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1 truncate">{product.product_type}</p>
              </div>
              <div className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 rounded-full border flex-shrink-0 ${getStatusColor(product.overall_status)}`}>
                {getStatusIcon(product.overall_status)}
                <span className="text-[11px] sm:text-sm font-medium uppercase">{product.overall_status}</span>
              </div>
            </div>

            {/* Temperature Stats */}
            <div className="space-y-3 mb-4 pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Latest Temperature</span>
                <span className={`text-lg font-bold ${
                  product.overall_status === 'critical' ? 'text-rose-600' :
                  product.overall_status === 'warning' ? 'text-amber-600' :
                  'text-emerald-600'
                }`}>
                  {product.latest_temperature.toFixed(1)}°C
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-slate-600">Min</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">
                  {product.min_temperature.toFixed(1)}°C
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-rose-500" />
                  <span className="text-sm text-slate-600">Max</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">
                  {product.max_temperature.toFixed(1)}°C
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Average</span>
                <span className="text-sm font-semibold text-slate-900">
                  {product.avg_temperature.toFixed(1)}°C
                </span>
              </div>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Readings</p>
                <p className="text-2xl font-bold text-slate-900">{product.reading_count}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Alarms</p>
                <p className="text-2xl font-bold text-slate-900">
                  {product.alarm_count}
                  {product.alarm_count > 0 && (
                    <span className="text-sm font-normal text-slate-600 ml-2">
                      ({product.critical_alarm_count}C / {product.warning_alarm_count}W)
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Session Info */}
            {product.test_session_id && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500">Monitoring Session: {product.test_session_id}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {testResults.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Thermometer className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No Product Data Available</h3>
          <p className="text-slate-600 mb-4">
            Product performance insights will appear here once monitoring data is recorded.
          </p>
          <p className="text-sm text-slate-500">
            Use Cold Chain Monitoring to submit temperature readings with product information.
          </p>
        </div>
      )}

      {/* Comparison View */}
      {comparisonData && comparisonData.products.length > 0 && (
        <div className="bg-white rounded-xl p-8 border border-slate-200">
          <h2 className="text-2xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope' }}>
            Product Comparison
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Metric
                  </th>
                  {comparisonData.products.map((product) => (
                    <th key={product.product_name} className="text-center py-3 px-4">
                      <div className="text-base font-bold text-slate-900">{product.product_name}</div>
                      <div className="text-xs text-slate-500 font-normal">{product.species_or_category}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Overall Status</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium uppercase ${getStatusColor(product.overall_status)}`}>
                        {product.overall_status}
                      </span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Latest Temperature</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-lg font-bold text-slate-900">{product.latest_temperature.toFixed(1)}°C</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Min Temperature</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-blue-600 font-semibold">{product.min_temperature.toFixed(1)}°C</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Max Temperature</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-rose-600 font-semibold">{product.max_temperature.toFixed(1)}°C</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Average Temperature</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-slate-900 font-semibold">{product.avg_temperature.toFixed(1)}°C</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Reading Count</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-slate-900">{product.reading_count}</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Total Alarms</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-slate-900 font-semibold">{product.alarm_count}</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Critical Alarms</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-rose-600 font-semibold">{product.critical_alarm_count}</span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-3 px-4 text-sm font-medium text-slate-700">Warning Alarms</td>
                  {comparisonData.products.map((product) => (
                    <td key={product.product_name} className="text-center py-3 px-4">
                      <span className="text-base text-amber-600 font-semibold">{product.warning_alarm_count}</span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-xs text-slate-500">
            Comparison generated at: {new Date(comparisonData.comparison_timestamp).toLocaleString('en-NO')}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestResults;
